// The look of the widget on the customer's own site.
//
// Everything offered here is something functions/lib/theme.js will accept:
// colors are hex, positions/shapes/fonts/modes are closed enums, and the text
// fields have the same caps the server truncates at. That is deliberate --
// theme.js replaces anything invalid with the default rather than rejecting it,
// so a form that let someone type `red` would silently ignore them.

import type { AgentTheme } from "../lib/types";
import { Field, Select, TextArea, TextInput } from "./ui";

const MODES = [
  { value: "auto", label: "Match the visitor's system setting" },
  { value: "light", label: "Always light" },
  { value: "dark", label: "Always dark" }
];

const POSITIONS = [
  { value: "bottom-right", label: "Floating, bottom right" },
  { value: "bottom-left", label: "Floating, bottom left" },
  { value: "inline", label: "Inline, wherever it's mounted" }
];

const SHAPES = [
  { value: "round", label: "Round" },
  { value: "squircle", label: "Rounded square" },
  { value: "square", label: "Square" }
];

const FONTS = [
  { value: "system", label: "System" },
  { value: "inter", label: "Inter" },
  { value: "serif", label: "Serif" },
  { value: "mono", label: "Monospace" }
];

type Props = {
  theme: AgentTheme;
  onChange: (theme: AgentTheme) => void;
  // Branding removal is a paid feature, enforced server-side in
  // theme.applyBrandingPolicy. Disabling the checkbox here is only so a free
  // customer is not offered a switch that silently does nothing.
  paid: boolean;
};

export default function ThemeEditor({ theme, onChange, paid }: Props) {
  function set<K extends keyof AgentTheme>(key: K, value: AgentTheme[K]) {
    onChange({ ...theme, [key]: value });
  }

  return (
    <div className="grid gap-5 sm:grid-cols-2">
      <Field label="Header title" hint="Up to 60 characters. Left as “Ask us anything”, the chat shows your business name, with that line under it.">
        <TextInput maxLength={60} value={theme.title} onChange={(e) => set("title", e.target.value)} />
      </Field>

      <Field label="Subtitle" hint="Optional. Up to 80 characters.">
        <TextInput maxLength={80} value={theme.subtitle} onChange={(e) => set("subtitle", e.target.value)} />
      </Field>

      <div className="sm:col-span-2">
        <Field label="Welcome message" hint="The first thing a visitor sees when the chat opens.">
          <TextArea
            rows={2}
            maxLength={300}
            value={theme.welcomeMessage}
            onChange={(e) => set("welcomeMessage", e.target.value)}
          />
        </Field>
      </div>

      <Field label="Input placeholder">
        <TextInput
          maxLength={60}
          value={theme.placeholder}
          onChange={(e) => set("placeholder", e.target.value)}
        />
      </Field>

      <Field label="Avatar image URL" hint="https only. Leave blank for no avatar.">
        <TextInput
          type="url"
          placeholder="https://example.com/logo.png"
          value={theme.avatarUrl}
          onChange={(e) => set("avatarUrl", e.target.value)}
        />
      </Field>

      <ColorField label="Primary color" value={theme.primaryColor} onChange={(v) => set("primaryColor", v)} />
      <ColorField label="Launcher color" value={theme.bubbleColor} onChange={(v) => set("bubbleColor", v)} />
      <ColorField label="Text color" value={theme.textColor} onChange={(v) => set("textColor", v)} />
      <ColorField label="Background" value={theme.backgroundColor} onChange={(v) => set("backgroundColor", v)} />

      <Field label="Color mode">
        <Select value={theme.mode} onChange={(e) => set("mode", e.target.value as AgentTheme["mode"])}>
          {MODES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </Field>

      <Field label="Position">
        <Select value={theme.position} onChange={(e) => set("position", e.target.value as AgentTheme["position"])}>
          {POSITIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </Field>

      <Field label="Launcher shape">
        <Select
          value={theme.bubbleShape}
          onChange={(e) => set("bubbleShape", e.target.value as AgentTheme["bubbleShape"])}
        >
          {SHAPES.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </Field>

      <Field label="Chat sounds" hint="Plays only while the chat is closed or the visitor is on another tab.">
        <Select value={theme.sound} onChange={(e) => set("sound", e.target.value as AgentTheme["sound"])}>
          <option value="person">Chime when someone from your team replies</option>
          <option value="off">No sounds</option>
        </Select>
      </Field>

      <Field label="Font">
        <Select
          value={theme.fontFamily}
          onChange={(e) => set("fontFamily", e.target.value as AgentTheme["fontFamily"])}
        >
          {FONTS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </Field>

      <div className="sm:col-span-2">
        <label className="flex items-start gap-3 text-sm text-slate-700">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600 disabled:cursor-not-allowed disabled:opacity-50"
            checked={theme.showBranding}
            disabled={!paid}
            onChange={(e) => set("showBranding", e.target.checked)}
          />
          <span>
            Show “Powered by Build Bots Now” in the widget
            {!paid && <em className="block text-xs not-italic text-slate-500">Removing it is part of the paid plan.</em>}
          </span>
        </label>
      </div>
    </div>
  );
}

function ColorField({
  label,
  value,
  onChange
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <Field label={label}>
      <div className="flex items-center gap-2">
        {/* Two controls over one value: the picker for choosing, the text box
            for pasting a brand hex out of a style guide. Both sit inside the
            same <label>, so each carries its own aria-label -- otherwise they
            are announced identically and only one of them is reachable by
            name. */}
        <input
          type="color"
          aria-label={`${label} picker`}
          className="h-9 w-12 cursor-pointer rounded border border-slate-300 bg-surface p-1 disabled:cursor-not-allowed disabled:opacity-50"
          value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : "#4F46E5"}
          onChange={(event) => onChange(event.target.value)}
        />
        <TextInput
          aria-label={`${label} hex`}
          value={value}
          spellCheck={false}
          onChange={(event) => onChange(event.target.value)}
          placeholder="#4F46E5"
        />
      </div>
    </Field>
  );
}
